import { useQuery } from "@tanstack/react-query";

import {
  createFileRoute,
  Link,
  Outlet,
  useNavigate,
} from "@tanstack/react-router";

import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

import { useMemo } from "react";

import type { InferTransformedProcedureOutput } from "@goose/api";

import type {
  ContactId,
  OpportunityId,
  OrganizationId,
} from "@goose/schema/Salesforce";

import { UUID } from "@goose/utils/uuid";

import { DataTable } from "~/components/DataTable";
import { QueryState } from "~/components/QueryState";
import { TRPCClientErrorDisplay } from "~/components/TRPCClientErrorDisplay";
import { Spinner } from "~/components/ui/Spinner";
import { formatDate } from "~/lib/format";
import { useTRPC } from "~/lib/trpc";

export const Route = createFileRoute(
  "/_authenticated/admin/organizations/$organizationId/opportunities/$opportunityId/contacts/$contactId/evaluations",
)({
  component: ContactEvaluations,
});

function ContactEvaluations() {
  const trpc = useTRPC();
  const { organizationId, opportunityId, contactId } = Route.useParams();

  const evaluations = useQuery(
    trpc.contacts.evaluations.list.queryOptions({
      organizationId,
      opportunityId,
      contactId,
    }),
  );

  return (
    <QueryState
      result={evaluations}
      loading={
        <div className="flex justify-center items-center py-24">
          <Spinner size="lg" />
        </div>
      }
      error={(error) => <TRPCClientErrorDisplay error={error} />}
    >
      {(data) => (
        <>
          <EvaluationsTable
            organizationId={organizationId}
            opportunityId={opportunityId}
            contactId={contactId}
            data={data}
          />
          <Outlet />
        </>
      )}
    </QueryState>
  );
}

type EvaluationsList =
  InferTransformedProcedureOutput<"contacts.evaluations.list">;

interface EvaluationsTableProps {
  organizationId: OrganizationId;
  opportunityId: OpportunityId;
  contactId: ContactId;
  data: EvaluationsList;
}

function EvaluationsTable({
  organizationId,
  opportunityId,
  contactId,
  data,
}: EvaluationsTableProps) {
  const navigate = useNavigate();

  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<EvaluationsList[number]>();
    return [
      columnHelper.accessor("createdAt", {
        header: "Created",
        cell: (ctx) => (
          <Link
            className="font-medium text-slate-900 underline transition-colors hover:text-slate-500"
            to="/admin/organizations/$organizationId/opportunities/$opportunityId/contacts/$contactId/evaluations/$evaluationId"
            params={{
              organizationId,
              opportunityId,
              contactId,
              evaluationId: UUID.fromString(ctx.row.original.id),
            }}
          >
            {formatDate(ctx.getValue())}
          </Link>
        ),
      }),
      columnHelper.accessor("score", {
        header: "Score",
        cell: (ctx) => {
          const score = ctx.getValue();
          return `${Math.round(score * 100)}%`;
        },
      }),
      columnHelper.accessor(
        (row) =>
          [row.createdBy.name, row.createdBy.email]
            .filter((part) => part != null && part.trim() !== "")
            .join(" "),
        {
          id: "createdBy",
          header: "Created By",
          cell: (ctx) => (
            <Link
              className="font-medium text-slate-900 underline transition-colors hover:text-slate-500"
              to="/admin/organizations/$organizationId/users/$userId"
              params={{
                organizationId: ctx.row.original.organizationId,
                userId: ctx.row.original.createdBy.id,
              }}
            >
              {ctx.row.original.createdBy.name ??
                ctx.row.original.createdBy.email}
            </Link>
          ),
        },
      ),
      columnHelper.accessor("clarification", {
        header: "Clarification",
        cell: (ctx) => ctx.getValue() ?? "—",
      }),
    ];
  }, [organizationId, opportunityId, contactId]);

  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data,
    columns,
    initialState: {
      sorting: [{ id: "createdAt", desc: true }],
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <DataTable
      title="Evaluations"
      emptyMessage="No evaluations found"
      table={table}
      onRowClick={(row) => {
        void navigate({
          to: "/admin/organizations/$organizationId/opportunities/$opportunityId/contacts/$contactId/evaluations/$evaluationId",
          params: {
            organizationId,
            opportunityId,
            contactId,
            evaluationId: UUID.fromString(row.original.id),
          },
        });
      }}
    />
  );
}
